/**
 * /personalization page content.
 * Deck-canonical (Q2 2026 Radium deck slides 8–10).
 *
 * The deck reframes Personalization around three pillars:
 *   Cf / 06 / Content        — Content intelligence
 *   Dc / 12 / Decisioning    — JTBD decisioning (Jobs-To-Be-Done)
 *   Gn / 07 / Generative     — Generative variants
 *
 * Admin (Ad/18) and Integrations (In/24) demote from top-level pillars
 * to sub-features inside the Decisioning + Stack-fit sections respectively.
 */

export const personalizationHero = {
  eyebrow: { symbol: 'Pe', number: '14', label: 'Personalization' },
  title: 'Personalization that knows the job.',
  tagline:
    'Generative recommendations on the surfaces you own. Aligned to Jobs-To-Be-Done, not just demographics.',
  description:
    "Radium Personalization turns enriched user signals into ranked, sequenced, in-session experiences. The decisioning layer reads the user's job-to-be-done, queries the content graph, and assembles micro-experiences in real time — with editorial governance, generative copy variants, and a feedback loop that closes in seconds.",
};

export interface JtbdPillar {
  symbol: string;
  number: string;
  label: string;
  title: string;
  body: string;
  anchor: string;
}

export const jtbdPillars: JtbdPillar[] = [
  {
    symbol: 'Cf',
    number: '06',
    label: 'Content',
    title: 'Content intelligence',
    body: 'Tag, embed, and structure your content library. Trained on your taxonomy. Editorial keeps the final word.',
    anchor: 'intelligence',
  },
  {
    symbol: 'Dc',
    number: '12',
    label: 'Decisioning',
    title: 'JTBD decisioning',
    body: 'Ranks and sequences against the job the user is hiring your product to do — not just who they are.',
    anchor: 'decisioning',
  },
  {
    symbol: 'Gn',
    number: '07',
    label: 'Generative',
    title: 'Generative variants',
    body: 'Copy, headlines, CTAs, and module compositions are generated and evaluated in-session, not pre-authored.',
    anchor: 'generative',
  },
];

/**
 * "Built for the post A/B test era." — deck slide 9.
 * Personalization comparison vs traditional optimization tools.
 */
export type PersonalizationSupportLevel = 'full' | 'partial' | 'none';

export interface PersonalizationCompareRow {
  capability: string;
  radium: PersonalizationSupportLevel;
  optimizely: PersonalizationSupportLevel;
  adobeTarget: PersonalizationSupportLevel;
  dynamicYield: PersonalizationSupportLevel;
  mutiny: PersonalizationSupportLevel;
}

export const personalizationCompareRows: PersonalizationCompareRow[] = [
  {
    capability: 'Generative content variants',
    radium: 'full',
    optimizely: 'partial',
    adobeTarget: 'partial',
    dynamicYield: 'partial',
    mutiny: 'full',
  },
  {
    capability: 'JTBD-aligned decisioning',
    radium: 'full',
    optimizely: 'none',
    adobeTarget: 'none',
    dynamicYield: 'partial',
    mutiny: 'partial',
  },
  {
    capability: 'Sub-second in-session ranking',
    radium: 'full',
    optimizely: 'partial',
    adobeTarget: 'partial',
    dynamicYield: 'full',
    mutiny: 'partial',
  },
  {
    capability: 'CMS-native content graph',
    radium: 'full',
    optimizely: 'none',
    adobeTarget: 'none',
    dynamicYield: 'none',
    mutiny: 'none',
  },
  {
    capability: 'Human-in-the-loop editorial admin',
    radium: 'full',
    optimizely: 'none',
    adobeTarget: 'none',
    dynamicYield: 'none',
    mutiny: 'none',
  },
  {
    capability: 'Open-source orchestration substrate',
    radium: 'full',
    optimizely: 'none',
    adobeTarget: 'none',
    dynamicYield: 'none',
    mutiny: 'none',
  },
  {
    capability: 'Stack-agnostic (Hightouch, Census, etc.)',
    radium: 'full',
    optimizely: 'partial',
    adobeTarget: 'partial',
    dynamicYield: 'partial',
    mutiny: 'partial',
  },
];

/**
 * Partnership phasing — deck slide 17.
 */
export const partnershipPhases = [
  {
    n: '01',
    title: 'Custom build (4–8 weeks)',
    body: 'We design and build around your stack — your CMS, your warehouse, your content model, your compliance requirements. No configuration template. Every deployment starts with your architecture.',
  },
  {
    n: '02',
    title: 'Deploy & hand off',
    body: 'We hand off a working, fully-hosted deployment. Your editorial team owns the admin layer. Your engineering team carries none of the infrastructure overhead. It runs before we leave.',
  },
  {
    n: '03',
    title: 'Platform subscription (ongoing)',
    body: 'The decisioning runtime runs on Radium infrastructure. Continuous model improvements, enterprise SLAs, and the Unicorn team on call when the next surface is ready to ship.',
  },
];
